/**
 * Pure visibility rules for a `WorkshopList` row.
 *
 * Every element on the row is independently switchable through an eleven-flag
 * contract, and ten of those flags are tri-state: `undefined` shows the
 * element when the data is there, `false` hides it no matter what, and `true`
 * shows it when the data is there — never a placeholder when it isn't.
 *
 * `showPacing` is the exception, and is off until asked for. The server sets
 * a status on every workshop, so an "auto" pacing flag would be a pill on
 * every row of every list that never asked for one.
 *
 * Pulled out as a plain function, with no React and no DOM, so the whole
 * switch matrix gets a fast, direct test instead of dozens of renders, and so
 * the row's own render logic stays under the file size and complexity caps.
 */

export interface WorkshopListSwitches {
  showVenue?: boolean;
  showTime?: boolean;
  showSpecies?: boolean;
  showSubregion?: boolean;
  showNames?: boolean;
  showSeal?: boolean;
  showRing?: boolean;
  showTag?: boolean;
  showSales?: boolean;
  showCount?: boolean;
  /** Off unless explicitly true — see the note at the top of this file. */
  showPacing?: boolean;
  /** The "mine" tag's text when a row carries no crew role (default "Yours"). */
  tagLabel?: string;
}

/** What a single row actually has to draw with, reduced to booleans — plus
 *  `mine`, which does double duty for the ring and the tag. */
export interface WorkshopListRowData {
  hasVenue: boolean;
  hasTime: boolean;
  hasSubregion: boolean;
  hasSpecies: boolean;
  hasTeacher: boolean;
  hasAssistant: boolean;
  mine: boolean;
  /** The row's own name for the viewer's part ("Teaching"…), when it has one. */
  roleLabel: string | null;
  hasSeal: boolean;
  hasCapacity: boolean;
  hasStatus: boolean;
  /** A custom `right` renderer was supplied — it replaces the sales row. */
  hasRight: boolean;
}

export interface WorkshopListVisibility {
  venue: boolean;
  time: boolean;
  subregion: boolean;
  species: boolean;
  teacherLine: boolean;
  assistantLine: boolean;
  seal: boolean;
  /** The "mine" ring — independent of the tag. */
  ring: boolean;
  /** The tag pill — independent of the ring. */
  tag: boolean;
  /** The row's crew role when it has one, else the caller's label, else
   *  "Yours". */
  tagLabel: string | null;
  /** The sales fill bar — independent of the count. */
  bar: boolean;
  /** The sold/cap count — independent of the bar. */
  count: boolean;
  pacing: boolean;
  /** Whether the row's bottom row renders at all — off when a custom `right`
   *  renderer is supplied, or when the bar, count and pacing flag are all
   *  quiet. */
  salesRow: boolean;
}

/** Tri-state resolution shared by every switch: `false` always wins, and
 *  otherwise the element shows only when there's something to show. */
function on(flag: boolean | undefined, has: boolean): boolean {
  return flag !== false && has;
}

export function resolveWorkshopListVisibility(
  switches: WorkshopListSwitches,
  data: WorkshopListRowData,
): WorkshopListVisibility {
  const teacherLine = on(switches.showNames, data.hasTeacher);
  const assistantLine = on(switches.showNames, data.hasAssistant);
  const tag = on(switches.showTag, data.mine);
  const bar = on(switches.showSales, data.hasCapacity);
  const count = on(switches.showCount, data.hasCapacity);
  const pacing = switches.showPacing === true && data.hasStatus;

  return {
    venue: on(switches.showVenue, data.hasVenue),
    time: on(switches.showTime, data.hasTime),
    subregion: on(switches.showSubregion, data.hasSubregion),
    species: on(switches.showSpecies, data.hasSpecies),
    teacherLine,
    assistantLine,
    seal: on(switches.showSeal, data.hasSeal),
    ring: on(switches.showRing, data.mine),
    tag,
    tagLabel: tag ? (data.roleLabel ?? switches.tagLabel ?? 'Yours') : null,
    bar,
    count,
    pacing,
    salesRow: !data.hasRight && (bar || count || pacing),
  };
}
